import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import { Slide } from 'react-reveal'

import Icon from '../../../../components/ui/Icon/Icon'
import TextInput from '../../../../components/ui/TextInput/TextInput'

import FrequentlyAskedItems from '../FrequentlyAskedItems/FrequentlyAskedItems'

import Loader from '../../../../assets/img/loader.gif'
import MoreHeaderParticles from '../../../../assets/img/more-header-particles.svg'

import * as HelpCenterApiService from '../../../../services/api/requests/HelpCenter'

import { MAIN, HELP_CENTER } from '../../../../services/data/profilePages.json'

const MainPage = styled.div`
  display: ${props => (props.show ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`

const Header = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-height: 87px;
  font-size: 0.7rem;
  background: var(--color-primary);

  &::before {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: url('${MoreHeaderParticles}');
    background-repeat: no-repeat;
    background-position: right top;
  }
`

const ReturnIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const Title = styled.h1`
  font-family: var(--font-family-ayala-display-sans);
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-right: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;
`

const Content = styled.div`
  flex: 1 1 auto;
  padding: 0;
  overflow: auto;
`

const SubjectListWrapper = styled.ul`
  position: relative;
  list-style: none;
  padding: 0;
`

const BlankContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

const StyledLoader = styled.img.attrs({ src: Loader })`
  max-height: 30px;
`

const DetailPageContainer = styled.div`
  display: ${props => (props.show ? 'block' : 'none')};
  background-color: inherit;
  transition-property: width;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-linear);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
`

const DetailPageContent = styled.div`
  padding: 2rem;
  overflow: auto;
`

const Subject = styled.h5`
  font-size: 1rem;
  margin: 0;
  margin-bottom: 0.2rem;
`

const Question = styled.h5`
  font-size: 1rem;
  color: var(--color-fg-light);
  margin: 0;
  margin-bottom: 1rem;
`

const Greetings = styled.p`
  text-align: center;
  margin-top: 0;
`

const DetailPageDescription = styled.div`
  figure {
    margin: 1rem 0;
  }

  img {
    max-width: 100%;
    margin: 0 auto;
  }
`

const SearchWrapper = styled.div`
  padding: 1.5rem;
`

const BlockTextInput = styled(TextInput)`
  width: 100%;
`

class FrequentlyAskedPage extends React.Component {
  state = {
    faqList: [],
    faqFilterList: [],
    searchKey: '',
    loadingStatus: null,
    errorMessage: null,
    selectedFAQ: null,
  }

  componentDidMount() {
    this.loadMessages()

    // check for postMessage for preselected FAQ
    window.addEventListener('message', ({ origin, data }) => {
      if (origin !== window.location.origin) return
      if (
        data?.type &&
        data.type === 'selectedFAQ' &&
        this.state.faqList.length > 0
      ) {
        const { category, item } = data

        const selectedCategory = this.state.faqList.find(
          c => c[category.key] === category.value,
        )
        const selectedItem =
          selectedCategory &&
          selectedCategory.items.find(i => i[item.key] === item.value)
        selectedItem && this.showDetailPage(selectedItem)

        window.removeEventListener('message')
      } else return
    })
  }

  showDetailPage = data => {
    this.setState({
      selectedFAQ: data,
    })
  }

  closeDetailPage = () => {
    this.setState({
      selectedFAQ: null,
    })
  }

  loadMessages = async () => {
    this.setState({
      loadingStatus: 0,
    })

    try {
      const faqResponse = await HelpCenterApiService.getAllFAQ()
      const urlParams = new URLSearchParams(window.location.search)

      this.setState({
        loadingStatus: 1,
        faqList: faqResponse.data,
        faqFilterList: faqResponse.data,
      })

      if (urlParams.has('q')) {
        const urlQuery = urlParams.get('q')
        this.searchFAQ({ target: { value: urlQuery } })
      } else {
        this.searchFAQ()
      }
    } catch (e) {
      this.setState({
        loadingStatus: 4,
        errorMessage: 'Unable to Load FAQs',
      })
    }
  }

  searchModifier = (value, search, type) => {
    let newValue = value || ''
    let newSearch = search || ''

    switch (type) {
      case 'trim-space':
        newValue = newValue.replace(/ /g, '')
        newSearch = newSearch.replace(/ /g, '')
        break
      case 'trim-special':
        newValue = newValue.replace(/[^a-zA-Z0-9]/g, '')
        newSearch = newSearch.replace(/[^a-zA-Z0-9]/g, '')
        break
      case 'special-to-space':
        newValue = newValue.replace(/[^a-zA-Z0-9]/g, ' ')
        newSearch = newSearch.replace(/[^a-zA-Z0-9]/g, ' ')
        break
      default:
        break
    }

    newValue = newValue.trim().toLowerCase()
    newSearch = newSearch.trim().toLowerCase()

    return newValue.includes(newSearch)
  }

  searchFAQ = e => {
    const { faqList } = this.state

    const { value: theQuery = '' } = (e || {}).target || {}

    const faqFilterList = faqList
      .map(faq => {
        const { name: theItemSubject, items } = faq

        const inSubject =
          this.searchModifier(theItemSubject, theQuery, 'trim-space') ||
          this.searchModifier(theItemSubject, theQuery, 'trim-special') ||
          this.searchModifier(theItemSubject, theQuery, 'special-to-space')

        const faqItems = inSubject
          ? items
          : items.filter(item => {
              const { question: theItemQuestion, answer: theItemAnswer } = item

              return (
                this.searchModifier(theItemQuestion, theQuery, 'trim-space') ||
                this.searchModifier(theItemAnswer, theQuery, 'trim-space') ||
                this.searchModifier(
                  theItemQuestion,
                  theQuery,
                  'trim-special',
                ) ||
                this.searchModifier(theItemAnswer, theQuery, 'trim-special') ||
                this.searchModifier(
                  theItemQuestion,
                  theQuery,
                  'special-to-space',
                ) ||
                this.searchModifier(theItemAnswer, theQuery, 'special-to-space')
              )
            })

        return {
          ...faq,
          items: faqItems,
          show: inSubject || faqItems.length,
        }
      })
      .filter(faq => faq.show)
      .sort((a, b) => a.order - b.order)

    this.setState({
      faqFilterList,
      searchKey: theQuery,
    })
  }

  displayItems = index => {
    const { faqFilterList } = this.state

    if (faqFilterList[index]) {
      faqFilterList[index].displayItems = !faqFilterList[index].displayItems

      this.setState({
        faqFilterList,
      })
    }
  }

  render() {
    const { loadingStatus, faqFilterList, selectedFAQ, searchKey } = this.state
    const { setProfileModalPage, primaryInfo, accessToken } = this.props
    const { firstName } = primaryInfo || {}

    const DetailPage = ({ data }) => {
      const show = !!data
      const { subject, question, answer } = data || {}

      return (
        <Slide right duration={200}>
          <DetailPageContainer show={show}>
            {!!show && (
              <>
                <Header>
                  <ReturnIcon onClick={this.closeDetailPage}>
                    <Icon name="slide-left" />
                  </ReturnIcon>
                  <Title>Frequently Asked Questions</Title>
                </Header>
                <DetailPageContent>
                  <Subject>{subject}</Subject>
                  <Question>{question}</Question>
                  <DetailPageDescription
                    dangerouslySetInnerHTML={{ __html: answer }}
                  />
                </DetailPageContent>
              </>
            )}
          </DetailPageContainer>
        </Slide>
      )
    }

    return (
      <>
        <MainPage show={!selectedFAQ}>
          <Header>
            <ReturnIcon
              onClick={() =>
                setProfileModalPage(accessToken ? HELP_CENTER : MAIN)
              }
            >
              <Icon name="slide-left" />
            </ReturnIcon>
            <Title>Frequently Asked Questions</Title>
          </Header>
          <Content>
            <SearchWrapper>
              <BlockTextInput
                placeholder="Search for solutions"
                onChange={this.searchFAQ}
                value={this.state.searchKey}
              />
            </SearchWrapper>
            <Greetings>
              {firstName ? `Hello ${firstName},` : 'Hi!'}
              <br />
              How can we help you?
            </Greetings>
            {loadingStatus === 0 && (
              <BlankContainer>
                <StyledLoader />
              </BlankContainer>
            )}
            <SubjectListWrapper key={searchKey}>
              {loadingStatus === 1 &&
                faqFilterList.map((props, index) => (
                  <FrequentlyAskedItems
                    key={props.id}
                    data={props}
                    index={index}
                    expand={!!searchKey}
                    showItems={this.displayItems}
                    showDetailPage={this.showDetailPage}
                  />
                ))}
            </SubjectListWrapper>
            {loadingStatus === 1 && faqFilterList.length === 0 && (
              <BlankContainer>No Records Found</BlankContainer>
            )}
          </Content>
        </MainPage>
        <DetailPage data={selectedFAQ} />
      </>
    )
  }
}

FrequentlyAskedPage.defaultProps = {
  primaryInfo: {},
}

FrequentlyAskedPage.propTypes = {
  primaryInfo: PropTypes.object,
}

export default FrequentlyAskedPage
